import LoadingScreen from '@/components/LoadingScreen'
import { useEffect, useState } from 'react'
import { useAppSelector } from '@/hooks/useRedux'
import { serverService, userService } from '@/services/api'
import type { Server, User } from '@/types'

export default function AdminPage() {
  const { user } = useAppSelector((state) => state.auth)
  const [activeTab, setActiveTab] = useState<'servers' | 'users' | 'roles'>('servers')
  const [servers, setServers] = useState<Server[]>([])
  const [users, setUsers] = useState<User[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    loadData()
  }, [activeTab])

  const loadData = async () => {
    setLoading(true)
    try {
      if (activeTab === 'servers') {
        const response = await serverService.list()
        const d = response.data
        setServers(Array.isArray(d) ? d : d?.servers ?? [])
      } else if (activeTab === 'users') {
        const response = await userService.list()
        const d = response.data
        setUsers(Array.isArray(d) ? d : d?.users ?? d?.data ?? [])
      }
    } catch (error) {
      console.error('Failed to load admin data:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleDeleteServer = async (serverId: string) => {
    if (!confirm('Are you sure you want to delete this server?')) return
    try {
      await serverService.delete(serverId)
      setServers(servers.filter((s) => s.id !== serverId))
    } catch (error) {
      console.error('Failed to delete server:', error)
      alert('Failed to delete server')
    }
  }

  const handleBanUser = async (userId: string) => {
    if (!confirm('Are you sure you want to ban this user?')) return
    try {
      await userService.ban(userId)
      setUsers(users.map((u) => (u.id === userId ? { ...u, banned: true } : u)))
    } catch (error) {
      console.error('Failed to ban user:', error)
      alert('Failed to ban user')
    }
  }

  if (!user || user.role !== 'admin') {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-900">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-red-500 mb-4">Access Denied</h1>
          <p className="text-gray-400">You need admin privileges to access this page.</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-900 p-8">
      <h1 className="text-4xl font-bold text-white mb-8">Admin Dashboard</h1>

      {/* Tabs */}
      <div className="flex gap-4 mb-8">
        {(['servers', 'users', 'roles'] as const).map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-6 py-2 rounded-lg font-semibold capitalize transition ${
              activeTab === tab
                ? 'bg-purple-600 text-white'
                : 'bg-gray-800 text-gray-400 hover:text-white'
            }`}
          >
            {tab}
          </button>
        ))}
      </div>

      {loading ? (
        <LoadingScreen variant="section" />
      ) : (
        <div className="bg-gray-800 rounded-lg p-6">
          {activeTab === 'servers' && (
            <div>
              <h2 className="text-2xl font-bold text-white mb-4">Manage Servers</h2>
              <div className="space-y-4">
                {servers.length === 0 ? (
                  <p className="text-gray-400">No servers found</p>
                ) : (
                  servers.map((server) => (
                    <div
                      key={server.id}
                      className="flex items-center justify-between bg-gray-700 p-4 rounded-lg"
                    >
                      <div>
                        <h3 className="text-lg font-semibold text-white">{server.name}</h3>
                        <p className="text-sm text-gray-400">
                          Owner: {server.owner_id} | Members: {server.member_count || 0}
                        </p>
                      </div>
                      <button
                        onClick={() => handleDeleteServer(server.id)}
                        className="bg-red-600 hover:bg-red-700 text-white font-bold py-2 px-4 rounded"
                      >
                        Delete
                      </button>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}

          {activeTab === 'users' && (
            <div>
              <h2 className="text-2xl font-bold text-white mb-4">Manage Users</h2>
              <div className="space-y-4">
                {users.length === 0 ? (
                  <p className="text-gray-400">No users found</p>
                ) : (
                  users.map((userItem) => (
                    <div
                      key={userItem.id}
                      className="flex items-center justify-between bg-gray-700 p-4 rounded-lg"
                    >
                      <div>
                        <h3 className="text-lg font-semibold text-white">
                          {userItem.username || userItem.email}
                        </h3>
                        <p className="text-sm text-gray-400">
                          Email: {userItem.email}
                          {userItem.banned && (
                            <span className="ml-2 text-red-500 font-bold">BANNED</span>
                          )}
                        </p>
                      </div>
                      {!userItem.banned && (
                        <button
                          onClick={() => handleBanUser(userItem.id)}
                          className="bg-red-600 hover:bg-red-700 text-white font-bold py-2 px-4 rounded"
                        >
                          Ban
                        </button>
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>
          )}

          {activeTab === 'roles' && (
            <div>
              <h2 className="text-2xl font-bold text-white mb-4">Role Management</h2>
              <p className="text-gray-400">Role management UI coming soon...</p>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
